import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { motion } from "framer-motion";
import { base44 } from "@/api/base44Client";
// Kept Play icon as it's used for video placeholders
import { ShoppingCart, Star, CheckCircle, Truck, Shield, CreditCard, ChevronLeft, ChevronRight, Info, Play, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRegion } from "@/components/RegionContext";
import { useLanguage } from "@/components/LanguageContext";
import { trackViewContent, trackAddToCart } from "@/lib/pixel";
import ProductInfographics from "@/components/product/ProductInfographics";
// TEMPORARILY REMOVED: import Product3DViewer from "../components/product/Product3DViewer";

// Product gallery is resolved per-language via useLanguage().getProductImages() below.

export default function ProductPage() {
  const [currentImage, setCurrentImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  // State to control loading of YouTube iframes
  const [showInstallationVideo, setShowInstallationVideo] = useState(false);
  const [showUsageVideo, setShowUsageVideo] = useState(false);

  const { formatLocalPrice, currencyCode, warehouse } = useRegion();
  const { t, getProductImages } = useLanguage();
  const productImages = getProductImages().gallery;

  const price = 399; // Updated base price
  const totalPrice = price * quantity;

  // Helper function to get YouTube video thumbnail
  const getYouTubeThumbnail = (videoId) => `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  // Helper function to extract YouTube video ID from URL
  const getYouTubeVideoId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const installationVideoId = getYouTubeVideoId("https://www.youtube.com/embed/qfPS2OD-ye8");
  const usageVideoId = getYouTubeVideoId("https://www.youtube.com/embed/ejXbB-KdL34"); // Updated usage video URL

  // Enhanced SEO for Product Page + Analytics
  useEffect(() => {
    document.title = "Buy LCTX Tire Changer $399 USD | Portable Manual Tire Changing Machine | Easy EZ Tire Changer | Air-Powered Tyre Changer | Free Worldwide Shipping";
    
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.name = 'description';
    metaDescription.content = "Buy LCTX Easy Tire Changer $399 USD. Professional manual EZ tire changer - portable air-powered tire changing machine for mechanics. No electricity. 2 ton torque, 12-21\" wheels. Free worldwide shipping!";
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(metaDescription);
    }

    // Add keywords meta tag
    let metaKeywords = document.querySelector('meta[name="keywords"]');
    if (!metaKeywords) {
      metaKeywords = document.createElement('meta');
      metaKeywords.name = 'keywords';
      document.head.appendChild(metaKeywords);
    }
    metaKeywords.content = "manual tire changer, easy tire changer, ez tire changer, tire changer, portable tire changer, air powered tire changer, pneumatic tire changer, tire changing machine, professional tire changer, mobile tire changer, LCTX tire changer";

    // Track product page view
    base44.analytics.track({
      eventName: "ez_tire_changer_product_view",
      properties: { page: "product", price: 399 }
    });
    // Meta conversion signal: shopper is viewing the product.
    trackViewContent();
  }, []);

  // Reset the selected gallery image when the language (and thus gallery set) changes
  useEffect(() => {
    setCurrentImage(0);
  }, [getProductImages]);

  return (
    <div className="bg-white dark:bg-gray-950 min-h-screen w-full overflow-x-hidden">
      {/* Enhanced SEO H1 */}
      <h1 className="sr-only">Buy LCTX Portable Tire Changer Machine | AED 1,465 / SAR 1,496 | Professional Manual Air-Powered Tyre Changing Tool | No Electricity Required | Free Shipping Across UAE & Saudi Arabia | 12-21 Inch Wheels | 2 Ton Bead Breaker | Mobile Mechanic Equipment</h1>

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 md:py-8 w-full">
        {/* Breadcrumb */}
        <div className="flex items-center gap-1 text-[9px] md:text-sm text-gray-600 mb-3 md:mb-6 flex-wrap">
          <Link to={createPageUrl("Home")} className="hover:text-red-600">{t("nav_home")}</Link>
          <span>/</span>
          <span className="text-gray-900 font-medium">{t("nav_product")}</span>
        </div>

        <div className="grid lg:grid-cols-2 gap-4 md:gap-10 mb-6 md:mb-12 w-full">
          {/* Image Gallery */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="w-full"
          >
            <div className="relative rounded-lg overflow-hidden bg-gray-50 mb-2 shadow-lg w-full">
              <div className="relative w-full" style={{ paddingBottom: '100%' }}>
                <img
                  key={currentImage}
                  src={productImages[currentImage]}
                  alt={`LCTX Tyre Changer - View ${currentImage + 1}`}
                  className="absolute inset-0 w-full h-full object-contain p-2"
                  width="800"
                  height="800"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  onError={(e) => {
                    console.error('Image failed to load:', productImages[currentImage]);
                    e.target.src = 'https://via.placeholder.com/800x800?text=LCTX+Tyre+Changer';
                  }}
                />
              </div>

              <button
                onClick={() => setCurrentImage((currentImage - 1 + productImages.length) % productImages.length)}
                className="absolute left-1 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1.5 rounded-full shadow-lg"
                aria-label="Previous image"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentImage((currentImage + 1) % productImages.length)}
                className="absolute right-1 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1.5 rounded-full shadow-lg"
                aria-label="Next image"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              <div className="absolute top-2 left-2">
                <Badge className="bg-green-500 hover:bg-green-500 text-white px-2 py-0.5 text-[8px] md:text-xs">
                  <Star className="w-2 h-2 md:w-3 md:h-3 mr-1 fill-white" />
                  Best Seller
                </Badge>
              </div>
            </div>

            <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-hide w-full">
              {productImages.map((img, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setCurrentImage(index);
                    base44.analytics.track({
                      eventName: "ez_tire_changer_image_view",
                      properties: { image_index: index + 1 }
                    });
                  }}
                  className={`flex-shrink-0 w-12 h-12 md:w-16 md:h-16 rounded-md overflow-hidden border-2 transition-all ${
                    currentImage === index
                      ? "border-red-600 ring-2 ring-red-200"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  <img
                    src={img}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                    width="100"
                    height="100"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/100x100?text=Image';
                    }}
                  />
                </button>
              ))}
            </div>
          </motion.div>

          {/* Product Info */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="space-y-3 md:space-y-6 w-full"
          >
            <div className="flex items-center gap-2 flex-wrap">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-3 h-3 md:w-5 md:h-5 fill-yellow-400 text-yellow-400" />
              ))}
              <span className="text-[10px] md:text-sm text-gray-600">{t("product_reviews")}</span>
            </div>

            <div>
              <h2 className="text-xl md:text-4xl lg:text-5xl font-black text-gray-900 mb-2 md:mb-4 leading-tight break-words">
                {t("product_title")}
              </h2>
              <p className="text-xs md:text-lg text-gray-700 leading-relaxed">
                {t("product_desc")}
              </p>
            </div>

            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-lg p-3 md:p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-lg md:text-2xl">🎉</span>
                <span className="text-xs md:text-base font-bold text-green-800">{t("product_offer_title")}</span>
              </div>
              <p className="text-[10px] md:text-sm text-green-700 font-semibold">
                {t("product_offer_text")}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 md:gap-4">
              {[
                { icon: CheckCircle, title: t("product_feat1_t"), subtitle: t("product_feat1_s") },
                { icon: CheckCircle, title: t("product_feat2_t"), subtitle: t("product_feat2_s") },
                { icon: CheckCircle, title: t("product_feat3_t"), subtitle: t("product_feat3_s") },
                { icon: CheckCircle, title: t("product_feat4_t"), subtitle: t("product_feat4_s") }
              ].map((feature, idx) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-2 md:p-3 border border-gray-200">
                  <div className="flex items-center gap-2">
                    <feature.icon className="w-3 h-3 md:w-5 md:h-5 text-green-600 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="font-bold text-gray-900 text-[10px] md:text-sm">{feature.title}</div>
                      <div className="text-[8px] md:text-xs text-gray-600">{feature.subtitle}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-b border-gray-200 py-3 md:py-6">
              <div className="flex items-baseline gap-2 flex-wrap mb-2">
                <span className="text-lg md:text-3xl font-bold text-gray-400 line-through">
                  {formatLocalPrice(699.99)}
                </span>
                <span className="text-2xl md:text-5xl font-black text-red-600">{formatLocalPrice(totalPrice)}</span>
                <Badge className="bg-red-600 hover:bg-red-600 text-white px-2 py-0.5 text-[8px] md:text-xs">
                  {t("product_save")}
                </Badge>
              </div>
              <p className="text-xs md:text-sm text-gray-600 font-semibold mb-2">
                {currencyCode} · {warehouse.id.toUpperCase()}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] md:text-sm text-gray-700">
                  ≈ ${totalPrice.toFixed(2)} USD
                </span>
              </div>
              <div className="flex items-center gap-2 mt-2">
                <CheckCircle className="w-3 h-3 md:w-5 md:h-5 text-green-600" />
                <span className="text-green-600 font-semibold text-[10px] md:text-sm">{t("product_instock")} — {warehouse.id.toUpperCase()}</span>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm md:text-sm font-bold text-gray-900 dark:text-white mb-2">{t("product_qty")}</label>
                <div className="flex items-center gap-3">
                  <motion.div whileTap={{ scale: 0.9 }} transition={{ duration: 0.1 }}>
                    <Button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 md:h-10 md:w-10 select-none dark:border-gray-600 dark:text-white dark:hover:bg-gray-800"
                    >
                      -
                    </Button>
                  </motion.div>
                  <motion.span 
                    key={quantity}
                    initial={{ scale: 1.2, color: '#dc2626' }}
                    animate={{ scale: 1, color: 'currentColor' }}
                    transition={{ duration: 0.15 }}
                    className="text-lg md:text-xl font-bold w-10 md:w-12 text-center dark:text-white"
                  >
                    {quantity}
                  </motion.span>
                  <motion.div whileTap={{ scale: 0.9 }} transition={{ duration: 0.1 }}>
                    <Button
                      onClick={() => setQuantity(quantity + 1)}
                      variant="outline"
                      size="icon"
                      className="h-10 w-10 md:h-10 md:w-10 select-none dark:border-gray-600 dark:text-white dark:hover:bg-gray-800"
                    >
                      +
                    </Button>
                  </motion.div>
                  <div className="flex-1 text-right">
                    <div className="text-sm md:text-sm text-gray-600 dark:text-gray-400">{t("product_total")}</div>
                    <div className="text-lg md:text-2xl font-black text-gray-900 dark:text-white">{formatLocalPrice(totalPrice)}</div>
                  </div>
                </div>
              </div>

              {/* BUY NOW BUTTON - Price removed, structural changes applied */}
              <div className="flex flex-col gap-3 md:gap-4">
                <div className="flex gap-3 md:gap-4">
                  <Link to={createPageUrl("Checkout")} className="flex-1" onClick={() => trackAddToCart()}>
                    <motion.div whileTap={{ scale: 0.97 }} transition={{ duration: 0.1 }}>
                      <Button
                        size="lg"
                        onClick={() => {
                          base44.analytics.track({
                            eventName: "ez_tire_changer_buy_now_clicked",
                            properties: { quantity: quantity, total_price: totalPrice, source: "product_page" }
                          });
                        }}
                        className="w-full bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800 text-white h-12 md:h-16 text-sm md:text-xl font-bold shadow-2xl select-none"
                      >
                        <ShoppingCart className="w-4 h-4 md:w-6 md:h-6 mr-2 select-none" />
                        {t("product_buy_now")}
                      </Button>
                    </motion.div>
                  </Link>
                </div>

                {/* COD Payment via WhatsApp - same width/height as the Buy Now button, WhatsApp green */}
                <Link to={`${createPageUrl("CODCheckout")}?quantity=${quantity}`} className="flex-1" onClick={() => {
                  base44.analytics.track({
                    eventName: "cod_payment_clicked",
                    properties: { quantity, total_price: totalPrice, source: "product_page" }
                  });
                }}>
                  <motion.div whileTap={{ scale: 0.97 }} transition={{ duration: 0.1 }}>
                    <Button
                      size="lg"
                      className="w-full bg-[#25D366] hover:bg-[#1FB857] text-white h-12 md:h-16 text-sm md:text-xl font-bold shadow-2xl select-none"
                    >
                      <MessageCircle className="w-4 h-4 md:w-6 md:h-6 mr-2 select-none" />
                      {t("cod_payment")}
                    </Button>
                  </motion.div>
                </Link>
              </div>

              <a
                href="https://www.youtube.com/watch?v=ejXbB-KdL34"
                target="_blank"
                rel="noopener noreferrer"
                className="relative block rounded-lg overflow-hidden shadow-lg border-2 border-gray-200 group aspect-video bg-black"
                onClick={() => {
                  base44.analytics.track({
                    eventName: "product_video_open",
                    properties: { video: "ejXbB-KdL34", source: "product_page" }
                  });
                }}
              >
                <img
                  src="https://img.youtube.com/vi/ejXbB-KdL34/hqdefault.jpg"
                  alt="LCTX Tire Changer - Usage Tutorial"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://img.youtube.com/vi/ejXbB-KdL34/mqdefault.jpg"; }}
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                  <div className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                    <svg className="w-7 h-7 text-white ml-1" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                </div>
                <span className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full">
                  Watch on YouTube
                </span>
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 pt-3 border-t text-[9px] md:text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <Truck className="w-3 h-3 md:w-4 md:h-4" />
                <span>{t("product_trust_ship")}</span>
              </div>
              <div className="flex items-center gap-1">
                <Shield className="w-3 h-3 md:w-4 md:h-4" />
                <span>{t("product_trust_back")}</span>
              </div>
              <div className="flex items-center gap-1">
                <CreditCard className="w-3 h-3 md:w-4 md:h-4" />
                <span>{t("product_trust_pay")}</span>
              </div>
            </div>

            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-3 md:p-4">
              <div className="flex items-start gap-2 md:gap-3">
                <Info className="w-4 h-4 md:w-5 md:h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-[10px] md:text-sm font-semibold text-red-900">
                    <strong>{t("product_notice_strong")}</strong> {t("product_notice_text")}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Uploaded product infographics - additional customer information */}
        <ProductInfographics />

        {/* NEW: YouTube Video Descriptions - PROFESSIONAL STYLING */}
        <section className="mb-12 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-2xl p-6 md:p-8 shadow-2xl border border-gray-700">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-8"
          >
            <div className="inline-block bg-red-600 text-white px-4 py-1.5 md:px-6 md:py-2 rounded-lg font-bold text-xs md:text-sm mb-4 shadow-lg">
              {t("product_video_badge")}
            </div>
            <h2 className="text-2xl md:text-4xl font-black text-white mb-3 md:mb-4">
              {t("product_video_heading")}
            </h2>
            <p className="text-sm md:text-lg text-gray-300 max-w-3xl mx-auto">
              {t("product_video_sub")}
            </p>
          </motion.div>

          {/* Installation Video + Description */}
          <div className="mb-8 md:mb-12">
            <div className="bg-gray-800 rounded-xl p-4 md:p-6 shadow-lg border border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-red-600 rounded-lg flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-white text-xl md:text-2xl">🔧</span>
                </div>
                <h3 className="text-xl md:text-3xl font-black text-white">
                  {t("product_install_heading")}
                </h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                <div className="aspect-video rounded-lg overflow-hidden shadow-xl ring-2 ring-gray-600 relative">
                  {showInstallationVideo ? (
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/qfPS2OD-ye8?autoplay=1" // Autoplay on click
                      title="LCTX Tire Changer Installation Tutorial"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="eager" // Load eagerly once user clicks
                    ></iframe>
                  ) : (
                    <div
                      className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black bg-opacity-70 group"
                      onClick={() => {
                        setShowInstallationVideo(true);
                        base44.analytics.track({
                          eventName: "video_tutorial_played",
                          properties: { 
                            video_type: "installation",
                            video_title: "LCTX Tire Changer Installation Tutorial",
                            source: "product_page"
                          }
                        });
                      }}
                      aria-label="Play LCTX Tire Changer Installation Tutorial"
                    >
                      {installationVideoId && (
                        <img
                          src={getYouTubeThumbnail(installationVideoId)}
                          alt="LCTX Tire Changer Installation Thumbnail"
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy" // Thumbnail can be lazy loaded
                        />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 group-hover:bg-opacity-60 transition-all">
                        <Play className="w-16 h-16 text-white bg-red-600 rounded-full p-2 shadow-lg group-hover:scale-110 transition-transform" />
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <div className="bg-gray-900 rounded-lg p-4 md:p-5 mb-4 border border-gray-700">
                    <h4 className="font-bold text-base md:text-lg text-white mb-2">
                      ⚙️ {t("product_install_box_t")}
                    </h4>
                    <p className="text-xs md:text-sm text-gray-300 leading-relaxed">
                      {t("product_install_box_p")}
                    </p>
                  </div>
                  <ul className="space-y-2 text-xs md:text-sm">
                    {[
                      t("product_install_s1"),
                      t("product_install_s2"),
                      t("product_install_s3"),
                      t("product_install_s4"),
                      t("product_install_s5"),
                      t("product_install_s6"),
                      t("product_install_s7"),
                    ].map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 bg-gray-900 rounded-lg p-2 md:p-3 border border-gray-700">
                        <span className="text-red-500 font-bold flex-shrink-0">✓</span>
                        <span className="text-gray-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-4 bg-blue-900/30 rounded-lg p-3 md:p-4 border border-blue-800">
                    <p className="text-xs md:text-sm font-semibold text-blue-200">
                      <span className="text-base md:text-xl">💡</span> {t("product_install_tip")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Usage Video + Description */}
          <div className="mb-6 md:mb-8">
            <div className="bg-gray-800 rounded-xl p-4 md:p-6 shadow-lg border border-gray-700">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 md:w-12 md:h-12 bg-red-600 rounded-lg flex items-center justify-center shadow-lg flex-shrink-0">
                  <span className="text-white text-xl md:text-2xl">🎯</span>
                </div>
                <h3 className="text-xl md:text-3xl font-black text-white">
                  {t("product_usage_heading")}
                </h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6 md:gap-8">
                <div className="aspect-video rounded-lg overflow-hidden shadow-xl ring-2 ring-gray-600 relative">
                  {showUsageVideo ? (
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/ejXbB-KdL34?autoplay=1" // Autoplay on click - UPDATED LINK
                      title="LCTX Tire Changer - Complete Usage Tutorial"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="eager" // Load eagerly once user clicks
                    ></iframe>
                  ) : (
                    <div
                      className="absolute inset-0 cursor-pointer flex items-center justify-center bg-black bg-opacity-70 group"
                      onClick={() => {
                        setShowUsageVideo(true);
                        base44.analytics.track({
                          eventName: "video_tutorial_played",
                          properties: { 
                            video_type: "usage",
                            video_title: "LCTX Tire Changer Usage Tutorial",
                            source: "product_page"
                          }
                        });
                      }}
                      aria-label="Play LCTX Tire Changer Usage Tutorial"
                    >
                      {usageVideoId && ( // Use the updated usageVideoId constant
                        <img
                          src={getYouTubeThumbnail(usageVideoId)}
                          alt="LCTX Tire Changer Usage Tutorial Thumbnail"
                          className="absolute inset-0 w-full h-full object-cover"
                          loading="lazy" // Thumbnail can be lazy loaded
                        />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 group-hover:bg-opacity-60 transition-all">
                        <Play className="w-16 h-16 text-white bg-red-600 rounded-full p-2 shadow-lg group-hover:scale-110 transition-transform" />
                      </div>
                    </div>
                  )}
                </div>
                <div>
                  <div className="bg-gray-900 rounded-lg p-4 md:p-5 mb-4 border border-gray-700">
                    <h4 className="font-bold text-base md:text-lg text-white mb-2">
                      🔄 {t("product_usage_box_t")}
                    </h4>
                    <p className="text-xs md:text-sm text-gray-300 leading-relaxed">
                      {t("product_usage_box_p")}
                    </p>
                  </div>
                  <ol className="space-y-2 text-xs md:text-sm">
                    {[
                      { step: t("product_usage_s1_s"), detail: t("product_usage_s1_d") },
                      { step: t("product_usage_s2_s"), detail: t("product_usage_s2_d") },
                      { step: t("product_usage_s3_s"), detail: t("product_usage_s3_d") },
                      { step: t("product_usage_s4_s"), detail: t("product_usage_s4_d") },
                      { step: t("product_usage_s5_s"), detail: t("product_usage_s5_d") },
                    ].map((item, idx) => (
                      <li key={idx} className="bg-gray-900 rounded-lg p-2 md:p-3 border border-gray-700">
                        <span className="font-bold text-red-500">{idx + 1}. {item.step}:</span>
                        <span className="text-gray-300 ml-2">{item.detail}</span>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-4 bg-green-900/30 rounded-lg p-3 md:p-4 border border-green-800">
                    <p className="text-xs md:text-sm font-semibold text-green-200">
                      <span className="text-base md:text-xl">⚡</span> {t("product_usage_tip")}
                    </p>
                  </div>
                  <div className="mt-3 bg-gray-900 rounded-lg p-3 md:p-4 border border-gray-700">
                    <p className="text-xs md:text-sm font-semibold text-gray-300">
                      <span className="text-base md:text-xl">✅</span> {t("product_usage_compat")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Call to Action - FIXED WIDTH */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
            className="text-center px-4"
          >
            <a
              href="https://www.youtube.com/@Eztirechanger?sub_confirmation=1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full max-w-md"
            >
              <Button size="lg" className="w-full bg-red-600 hover:bg-red-700 text-white h-12 md:h-14 px-6 md:px-8 text-sm md:text-lg font-bold shadow-xl">
                <Play className="w-4 h-4 md:w-5 md:h-5 mr-2 flex-shrink-0" />
                <span className="truncate">{t("product_video_cta")}</span>
              </Button>
            </a>
            <p className="mt-3 text-xs md:text-sm text-gray-400">
              🎥 {t("product_video_cta_sub")}
            </p>
          </motion.div>
        </section>

        {/* Product Description Tabs */}
        <Tabs defaultValue="overview" className="mb-8 md:mb-12">
          <TabsList className="grid w-full grid-cols-3 h-auto bg-gray-100 p-1 rounded-lg">
            <TabsTrigger value="overview" className="text-[10px] md:text-base font-bold py-2 md:py-3">{t("product_tab_overview")}</TabsTrigger>
            <TabsTrigger value="specs" className="text-[10px] md:text-base font-bold py-2 md:py-3">{t("product_tab_specs")}</TabsTrigger>
            <TabsTrigger value="faq" className="text-[10px] md:text-base font-bold py-2 md:py-3">{t("product_tab_faq")}</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-4 md:mt-6 space-y-4 md:space-y-6">
            <Card>
              <CardContent className="p-4 md:p-6">
                <h3 className="text-base md:text-2xl font-black mb-3 md:mb-4">{t("product_overview_h")}</h3>
                <div className="space-y-3 md:space-y-4 text-xs md:text-base text-gray-700 leading-relaxed">
                  <p>
                    {t("product_overview_p1")}
                  </p>
                  <p>
                    <strong>{t("product_overview_benefits")}</strong>
                  </p>
                  <ul className="list-disc list-inside space-y-2 md:space-y-3">
                    <li>{t("product_benefit1")}</li>
                    <li>{t("product_benefit2")}</li>
                    <li>{t("product_benefit3")}</li>
                    <li>{t("product_benefit4")}</li>
                    <li>{t("product_benefit5")}</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="specs" className="mt-4 md:mt-6">
            <Card>
              <CardContent className="p-4 md:p-6">
                <h3 className="text-base md:text-2xl font-black mb-3 md:mb-4">{t("product_specs_h")}</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-[10px] md:text-sm">
                    <tbody className="divide-y divide-gray-200">
                      {[
                        [t("product_spec1_l"), t("product_spec1_v")],
                        [t("product_spec2_l"), t("product_spec2_v")],
                        [t("product_spec3_l"), t("product_spec3_v")],
                        [t("product_spec4_l"), t("product_spec4_v")],
                        [t("product_spec5_l"), t("product_spec5_v")],
                        [t("product_spec6_l"), t("product_spec6_v")],
                        [t("product_spec7_l"), t("product_spec7_v")],
                        [t("product_spec8_l"), t("product_spec8_v")],
                      ].map(([label, value], idx) => (
                        <tr key={idx}>
                          <td className="py-2 md:py-3 font-semibold text-gray-900">{label}</td>
                          <td className="py-2 md:py-3 text-gray-700">{value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="faq" className="mt-4 md:mt-6">
            <Accordion type="single" collapsible className="space-y-3">
              {[
                {
                  q: t("product_faq1_q"),
                  a: t("product_faq1_a"),
                },
                {
                  q: t("product_faq2_q"),
                  a: t("product_faq2_a"),
                },
                {
                  q: t("product_faq3_q"),
                  a: t("product_faq3_a"),
                },
                {
                  q: t("product_faq4_q"),
                  a: t("product_faq4_a"),
                },
                {
                  q: t("product_faq5_q"),
                  a: t("product_faq5_a"),
                }
              ].map((faq, idx) => (
                <AccordionItem key={idx} value={`faq-${idx}`} className="bg-white border-2 border-gray-200 rounded-lg overflow-hidden">
                  <AccordionTrigger className="px-3 md:px-4 py-2 md:py-3 hover:no-underline text-[10px] md:text-base text-left">
                    <span className="font-bold text-gray-900">{faq.q}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-3 md:px-4 pb-3 md:pb-4 text-[10px] md:text-sm text-gray-700">
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </TabsContent>
        </Tabs>

        {/* 3D Product Viewer - TEMPORARILY DISABLED TO FIX LOADING ISSUES */}
        {/* <Product3DViewer /> */}

        {/* Parts List Section */}
        <section className="py-8 md:py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white rounded-2xl md:rounded-3xl mb-8 md:mb-16">
          <div className="px-3 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-6 md:mb-12"
            >
              <h2 className="text-2xl md:text-4xl font-black mb-2 md:mb-4">{t("product_parts_h")}</h2>
              <p className="text-xs md:text-xl text-gray-300 max-w-3xl mx-auto">
                {t("product_parts_sub")}
              </p>
            </motion.div>

            {/* Desktop View */}
            <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { num: 1, name: t("product_part_1") },
                { num: 2, name: t("product_part_2") },
                { num: 3, name: t("product_part_3") },
                { num: 4, name: t("product_part_4") },
                { num: 5, name: t("product_part_5") },
                { num: 6, name: t("product_part_6") },
                { num: 7, name: t("product_part_7") },
                { num: 8, name: t("product_part_8") },
                { num: 9, name: t("product_part_9") },
                { num: 10, name: t("product_part_10") },
                { num: 11, name: t("product_part_11") },
                { num: 12, name: t("product_part_12") },
                { num: 13, name: t("product_part_13") },
                { num: 14, name: t("product_part_14") }
              ].map((part, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 hover:bg-white/15 transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                      <span className="text-2xl font-black">{part.num}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-lg leading-tight break-words">{part.name}</h3>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Mobile View - Accordion */}
            <div className="md:hidden">
              <Accordion type="single" collapsible className="space-y-3">
                <AccordionItem value="parts-list" className="bg-white/10 backdrop-blur-sm rounded-lg border border-white/20">
                  <AccordionTrigger className="px-4 py-3 hover:no-underline">
                    <span className="text-sm font-bold text-white">{t("product_parts_mobile")}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-2 pt-2">
                      {[
                        { num: 1, name: "Hub Adjustment Assembly" },
                        { num: 2, name: "Large Cylinder and Foot Pedal Assembly" },
                        { num: 3, name: "Push Rod Assembly" },
                        { num: 4, name: "Bead Breaker Arm Assembly" },
                        { num: 5, name: "Cone for Securing the Wheel Hub Centre Hole" },
                        { num: 6, name: "Large Nut for Locking the Cone" },
                        { num: 7, name: "Wheel Hub Securing and Rotation Assembly" },
                        { num: 8, name: "Tyre Pry Bar" },
                        { num: 9, name: "Stabilising Stand for the Tyre Changer" },
                        { num: 10, name: "Bracket for Mounting the Large Cylinder" },
                        { num: 11, name: "Screws for Mounting the Bead Breaker Arm" },
                        { num: 12, name: "Screws for Mounting the Large Cylinder" },
                        { num: 13, name: "Hex Wrench" },
                        { num: 14, name: "Crescent Locking Wrench" }
                      ].map((part, index) => (
                        <div key={index} className="bg-white/5 rounded-lg p-3 flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 bg-red-600 rounded-lg flex items-center justify-center">
                            <span className="text-base font-black">{part.num}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-xs leading-tight text-white break-words">{part.name}</h3>
                          </div>
                        </div>
                      ))}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="mt-6 md:mt-12 bg-red-600/20 border-2 border-red-500 rounded-xl p-4 md:p-8 text-center"
            >
              <p className="text-xs md:text-lg font-semibold">
                📦 <strong>{t("product_parts_notice")}</strong>
              </p>
            </motion.div>
          </div>
        </section>

        {/* Installation, Remove, Install sections with same font sizes */}
        <section className="mb-8 md:mb-16">
          <div className="text-center mb-6 md:mb-12">
            <h2 className="text-2xl md:text-4xl font-black mb-2 md:mb-4">{t("product_guide_h")}</h2>
            <p className="text-xs md:text-xl text-gray-600">{t("product_guide_sub")}</p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            <AccordionItem value="installation" className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden shadow-lg">
              <AccordionTrigger className="px-4 md:px-6 py-3 md:py-4 hover:no-underline">
                <h3 className="text-base md:text-2xl font-black text-gray-900">📋 {t("product_guide_inst_h")}</h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                <div className="space-y-3 md:space-y-4 text-xs md:text-base text-gray-700">
                  <p>{t("product_guide_inst_p")}</p>
                  <ol className="list-decimal list-inside space-y-2 md:space-y-3">
                    <li>{t("product_guide_inst_1")}</li>
                    <li>{t("product_guide_inst_2")}</li>
                    <li>{t("product_guide_inst_3")}</li>
                    <li>{t("product_guide_inst_4")}</li>
                    <li>{t("product_guide_inst_5")}</li>
                  </ol>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="remove" className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden shadow-lg">
              <AccordionTrigger className="px-4 md:px-6 py-3 md:py-4 hover:no-underline">
                <h3 className="text-base md:text-2xl font-black text-gray-900">🔧 {t("product_guide_rem_h")}</h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                <div className="space-y-3 md:space-y-4 text-xs md:text-base text-gray-700">
                  <ol className="list-decimal list-inside space-y-2 md:space-y-3">
                    <li>{t("product_guide_rem_1")}</li>
                    <li>{t("product_guide_rem_2")}</li>
                    <li>{t("product_guide_rem_3")}</li>
                    <li>{t("product_guide_rem_4")}</li>
                    <li>{t("product_guide_rem_5")}</li>
                    <li>{t("product_guide_rem_6")}</li>
                  </ol>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="install" className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden shadow-lg">
              <AccordionTrigger className="px-4 md:px-6 py-3 md:py-4 hover:no-underline">
                <h3 className="text-base md:text-2xl font-black text-gray-900">✅ {t("product_guide_ins_h")}</h3>
              </AccordionTrigger>
              <AccordionContent className="px-4 md:px-6 pb-4 md:pb-6">
                <div className="space-y-3 md:space-y-4 text-xs md:text-base text-gray-700">
                  <ol className="list-decimal list-inside space-y-2 md:space-y-3">
                    <li>{t("product_guide_ins_1")}</li>
                    <li>{t("product_guide_ins_2")}</li>
                    <li>{t("product_guide_ins_3")}</li>
                    <li>{t("product_guide_ins_4")}</li>
                    <li>{t("product_guide_ins_5")}</li>
                    <li>{t("product_guide_ins_6")}</li>
                    <li>{t("product_guide_ins_7")}</li>
                  </ol>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>

        {/* PDF Manual Download Section */}
        <section className="py-8 md:py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white rounded-2xl md:rounded-3xl mb-8 md:mb-16">
          <div className="px-3 sm:px-6 lg:px-8 max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 md:w-20 md:h-20 bg-red-600 rounded-xl md:rounded-2xl mb-3 md:mb-6 shadow-2xl mx-auto">
                <svg className="w-6 h-6 md:w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>

              <h2 className="text-xl md:text-4xl font-black mb-2 md:mb-4">{t("product_manual_h")}</h2>
              <p className="text-[10px] md:text-lg text-gray-300 mb-4 md:mb-8 max-w-2xl mx-auto">
                {t("product_manual_p")}
              </p>

              <a
                href="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/68e21bb4c1e25d4ab005735e/2e2ba24fe_.pdf"
                download="LCTX-Tyre-Changer-User-Manual.pdf"
                className="inline-block"
                onClick={() => {
                  base44.analytics.track({
                    eventName: "ez_tire_changer_manual_downloaded",
                    properties: { manual_type: "pdf" }
                  });
                }}
              >
                <Button size="lg" className="bg-white text-gray-900 hover:bg-gray-100 h-10 md:h-16 px-6 md:px-12 text-xs md:text-xl font-bold shadow-2xl">
                  <svg className="w-4 h-4 md:w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                  </svg>
                  Download PDF Manual
                </Button>
              </a>

              <p className="mt-3 md:mt-6 text-[9px] md:text-sm text-gray-400">
                📄 File size: 2.5 MB • Format: PDF • Language: English
              </p>
            </motion.div>
          </div>
        </section>

        {/* Final CTA - Price removed and updated according to outline */}
        <section className="py-12 md:py-16 bg-gradient-to-br from-gray-900 to-black text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl md:text-4xl font-black mb-3 md:mb-4">
            {t("product_final_h")}
            </h2>
            <p className="text-sm md:text-xl text-gray-300 mb-6 md:mb-8">
            {t("product_final_sub")}
            </p>

            <Link to={createPageUrl("Checkout")} onClick={() => trackAddToCart()}>
              <Button 
                size="lg" 
                onClick={() => {
                  base44.analytics.track({
                    eventName: "ez_tire_changer_order_now_clicked",
                    properties: { source: "bottom_cta" }
                  });
                }}
                className="bg-red-600 hover:bg-red-700 text-white px-6 md:px-8 py-4 md:py-5 text-base md:text-lg font-bold rounded-full shadow-2xl"
              >
                <ShoppingCart className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                {t("order_now")}
              </Button>
            </Link>

            <p className="mt-4 md:mt-6 text-xs md:text-base text-gray-400">
              {t("product_final_footer")}
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}